// Guia interativo da Área do Cliente (porte do Portal da Cidade): clareia o próximo elemento, liga o
// balão a ele com uma linha que se desenha e o contorna. Mesmo comportamento; só textos e cores mudam.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import TextoSuporte from "./TextoSuporte";
import {
  acharAlvo,
  alvoPeloTexto,
  assinar,
  cliqueConclui,
  fecharGuia,
  interpretar,
  irParaPasso,
  lerEstado,
  posicaoDoBalao,
} from "./guia";
import css from "./GuiaInterativo.module.css";
const ESPERA_MS = 4000;
const FOLGA = 6;
const LARGURA = 320;
const DICA = { grupo: "Primeiro, abra este grupo do menu.", gaveta: "Primeiro, abra o menu." };
// Ponto da caixa mais perto de (x, y).
const pertoDe = (c, x, y) => ({
  x: Math.min(Math.max(x, c.left), c.left + c.width),
  y: Math.min(Math.max(y, c.top), c.top + c.height),
});
export default function GuiaInterativo() {
  const bruto = useSyncExternalStore(assinar, lerEstado, () => null);
  const estado = useMemo(() => interpretar(bruto), [bruto]);
  const [alvo, setAlvo] = useState(null);
  const [caixa, setCaixa] = useState(null);
  const [tela, setTela] = useState(() => ({
    w: typeof window === "undefined" ? 0 : window.innerWidth,
    h: typeof window === "undefined" ? 0 : window.innerHeight,
    bw: LARGURA,
    bh: 200,
  }));
  const [naoAchou, setNaoAchou] = useState(false);
  const [entrou, setEntrou] = useState(false);
  const balaoRef = useRef(null);
  const passo = estado?.guia.passos[estado.passo];
  const ativo = !!estado;
  // Ao abrir, o balão nasce no canto do chat e desliza no quadro seguinte.
  useEffect(() => {
    if (!ativo) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntrou(true)));
    return () => {
      cancelAnimationFrame(id);
      setEntrou(false);
    };
  }, [ativo]);
  // Procura o alvo de tempos em tempos e ao rolar/redimensionar: acompanha troca de tela, modais e menu abrindo.
  useEffect(() => {
    if (!passo) return;
    const inicio = Date.now();
    let rolou = null;
    const medir = () => {
      const b = balaoRef.current;
      setTela((t) => {
        const n = {
          w: window.innerWidth,
          h: window.innerHeight,
          bw: b?.offsetWidth || LARGURA,
          bh: b?.offsetHeight || 200,
        };
        return n.w === t.w && n.h === t.h && n.bw === t.bw && n.bh === t.bh ? t : n;
      });
      // [guia=fora]: passo fora da Área do Cliente (e-mail, registrador, login). Sem alvo e sem aviso: balão no centro.
      const a = passo.alvo === "fora" ? null : passo.alvo ? acharAlvo(passo.alvo) : alvoPeloTexto(passo.texto);
      setAlvo(a);
      if (!a) {
        setCaixa(null);
        setNaoAchou(!!passo.alvo && passo.alvo !== "fora" && Date.now() - inicio > ESPERA_MS);
        return;
      }
      if (rolou !== a.el) {
        rolou = a.el;
        a.el.scrollIntoView?.({ block: "center", behavior: "smooth" });
      }
      const r = a.el.getBoundingClientRect();
      setNaoAchou(false);
      setCaixa((c) =>
        c && c.top === r.top && c.left === r.left && c.width === r.width && c.height === r.height
          ? c
          : { top: r.top, left: r.left, width: r.width, height: r.height },
      );
    };
    const primeira = setTimeout(medir, 0);
    const id = setInterval(medir, 250);
    window.addEventListener("scroll", medir, true);
    window.addEventListener("resize", medir);
    return () => {
      clearTimeout(primeira);
      clearInterval(id);
      window.removeEventListener("scroll", medir, true);
      window.removeEventListener("resize", medir);
    };
  }, [passo]);
  // Clique no próprio alvo conclui o passo; Esc fecha o guia.
  useEffect(() => {
    if (!estado) return;
    const clique = (e) => {
      if (cliqueConclui(alvo, e.target)) setTimeout(() => irParaPasso(estado, estado.passo + 1), 0);
    };
    const tecla = (e) => {
      if (e.key === "Escape") fecharGuia();
    };
    document.addEventListener("click", clique, true);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("click", clique, true);
      document.removeEventListener("keydown", tecla);
    };
  }, [estado, alvo]);
  if (!estado || !passo || typeof document === "undefined") return null;
  const { guia } = estado;
  const ultimo = estado.passo === guia.passos.length - 1;
  const { w: vw, h: vh, bw, bh } = tela;
  const recorte = caixa && {
    top: caixa.top - FOLGA,
    left: caixa.left - FOLGA,
    width: caixa.width + FOLGA * 2,
    height: caixa.height + FOLGA * 2,
  };
  // Sem alvo: centro da tela. Antes de entrar: canto do chat (inferior direito).
  const pos = !entrou
    ? { left: vw - bw - 24, top: vh - bh - 24 }
    : recorte
      ? posicaoDoBalao(recorte, bw, bh, vw, vh)
      : { left: Math.max(12, (vw - bw) / 2), top: Math.max(12, (vh - bh) / 2) };
  const caixaBalao = { ...pos, width: bw, height: bh };
  const ini = recorte && pertoDe(caixaBalao, recorte.left + recorte.width / 2, recorte.top + recorte.height / 2);
  const fim = recorte && ini && pertoDe(recorte, ini.x, ini.y);
  // O clareamento lê a posição do alvo por variáveis; o desenho fica no CSS Module.
  const luz = recorte
    ? {
        "--guia-rx": `${recorte.width / 2 + 140}px`,
        "--guia-ry": `${recorte.height / 2 + 140}px`,
        "--guia-cx": `${recorte.left + recorte.width / 2}px`,
        "--guia-cy": `${recorte.top + recorte.height / 2}px`,
      }
    : undefined;
  return createPortal(
    <div className={css.camada} data-guia-interativo>
      <div className={recorte ? css.clareamento : css.escurecido} style={luz} />
      {recorte && ini && fim && entrou && (
        <svg key={`${estado.passo}-${alvo?.dica ?? ""}`} aria-hidden="true" className={css.linhas}>
          <path
            data-guia-linha
            className={css.linha}
            pathLength={1}
            d={`M${ini.x},${ini.y} L${fim.x},${fim.y}`}
            fill="none"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </svg>
      )}
      {recorte && entrou && <div key={`r-${estado.passo}`} data-guia-recorte className={css.recorte} style={recorte} />}

      <div
        ref={balaoRef}
        role="dialog"
        aria-label="Guia interativo"
        aria-live="polite"
        className={css.balao}
        style={{
          top: pos.top,
          left: pos.left,
          width: LARGURA,
          opacity: entrou ? 1 : 0,
          transform: entrou ? "none" : "scale(.9)",
        }}
      >
        <p className={css.contador}>
          Passo {estado.passo + 1} de {guia.passos.length}
        </p>
        {alvo?.dica && <p className={css.dica}>{DICA[alvo.dica]}</p>}
        <TextoSuporte texto={passo.texto} />
        {naoAchou && (
          <p className={css.naoAchou}>Não achei esse ponto nesta tela. Siga pelo texto ou vá para o próximo passo.</p>
        )}
        <div className={css.acoes}>
          {estado.passo > 0 && (
            <button type="button" onClick={() => irParaPasso(estado, estado.passo - 1)} className={css.voltar}>
              Voltar
            </button>
          )}
          <button type="button" autoFocus onClick={() => irParaPasso(estado, estado.passo + 1)} className={css.avancar}>
            {ultimo ? "Concluir" : "Próximo"}
          </button>
        </div>
      </div>

      <button type="button" onClick={fecharGuia} className={css.fechar}>
        Fechar esse guia interativo
      </button>
    </div>,
    document.body,
  );
}
